import { useEffect, useRef } from 'react';
import { decodePolyline, type GeoPoint } from '@app/shared/geo';
import { googleAuthFailed, literal, loadGoogleMaps, onGoogleAuthFailure, type GMap, type GMarker, type GoogleMapsNs, type GPolyline, type MapsListener } from './googleMaps';

/** Center of Poland: where an empty map starts. */
const POLAND: GeoPoint = { lat: 52.07, lng: 19.48 };
/** 6 decimals: about 10 cm, plenty for a pin. */
const round = (p: GeoPoint): GeoPoint => ({ lat: Math.round(p.lat * 1e6) / 1e6, lng: Math.round(p.lng * 1e6) / 1e6 });

/** A route drawn on the map ("Pokaż trasę"): its encoded line; the chosen one stands out. */
export type MapRoute = { key: string; polyline: string; selected: boolean };

export type GoogleMapProps = {
  point: GeoPoint | null;
  center?: GeoPoint | null | undefined;
  onPick?: ((p: GeoPoint) => void) | undefined;
  label: string;
  wheelZoom?: boolean | undefined;
  centerZoom?: number | undefined;
  className?: string | undefined;
  routes?: MapRoute[] | undefined;
  onRouteClick?: ((key: string) => void) | undefined;
  apiKey: string;
  mapId: string;
  /** Google refused the key or did not load: the caller shows the OpenStreetMap map instead. */
  onFail: () => void;
};

/**
 * A Google map (spec 2026-10-22-mapy-google §3.4; Google's terms: places and routes from Google are shown on a Google
 * map): the pin of the place (dragged or put by a click when `onPick`), and the routes of "Pokaż trasę" — the chosen
 * one on top. The same props as the OpenStreetMap map, so the screens do not care which one they get.
 */
export function GoogleMap({ point, center, onPick, label, wheelZoom = false, centerZoom = 9, className = 'map-preview', routes, onRouteClick, apiKey, mapId, onFail }: GoogleMapProps) {
  const box = useRef<HTMLDivElement>(null);
  const state = useRef<{ g: GoogleMapsNs; map: GMap; marker: GMarker | null; lines: GPolyline[] } | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;
  const routeClick = useRef(onRouteClick);
  routeClick.current = onRouteClick;
  const fail = useRef(onFail);
  fail.current = onFail;
  const latest = useRef({ point, routes });
  latest.current = { point, routes };

  useEffect(() => {
    let cancelled = false;
    const listeners: MapsListener[] = [];
    const off = onGoogleAuthFailure(() => fail.current());
    if (googleAuthFailed()) {
      fail.current();
      return off;
    }
    loadGoogleMaps(apiKey)
      .then((g) => {
        if (cancelled || !box.current) return;
        const start = latest.current.point ?? center ?? POLAND;
        const map = new g.Map(box.current, {
          center: start,
          zoom: latest.current.point ? 15 : center ? centerZoom : 6,
          mapId,
          gestureHandling: wheelZoom ? 'greedy' : 'cooperative',
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });
        if (pick.current) listeners.push(map.addListener('click', (e) => e.latLng && pick.current?.(round(literal(e.latLng)))));
        state.current = { g, map, marker: null, lines: [] };
        place(latest.current.point, false);
        draw(latest.current.routes);
      })
      .catch(() => {
        if (!cancelled) fail.current();
      });
    return () => {
      cancelled = true;
      off();
      for (const l of listeners) l.remove();
      const s = state.current;
      if (s?.marker) s.marker.map = null;
      for (const line of s?.lines ?? []) line.setMap(null);
      state.current = null;
    };
    // the map itself, once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function place(p: GeoPoint | null, pan = true) {
    const s = state.current;
    if (!s) return;
    if (!p) {
      if (s.marker) s.marker.map = null;
      s.marker = null;
      return;
    }
    if (!s.marker) {
      const marker = new s.g.marker.AdvancedMarkerElement({ map: s.map, position: p, title: label, gmpDraggable: !!pick.current });
      marker.addListener('dragend', () => {
        if (marker.position) pick.current?.(round(literal(marker.position)));
      });
      s.marker = marker;
    } else s.marker.position = p;
    if (pan && !latest.current.routes?.length) s.map.panTo(p);
  }

  function draw(list: MapRoute[] | undefined) {
    const s = state.current;
    if (!s) return;
    for (const line of s.lines) line.setMap(null);
    s.lines = [];
    if (!list || list.length === 0) return;
    const bounds = new s.g.LatLngBounds();
    // the others under, the chosen one on top
    for (const r of [...list].sort((a, b) => Number(a.selected) - Number(b.selected))) {
      const path = decodePolyline(r.polyline);
      for (const p of path) bounds.extend(p);
      const line = new s.g.Polyline({ path, map: s.map, strokeColor: r.selected ? '#1a73e8' : '#7d8794', strokeOpacity: r.selected ? 0.95 : 0.6, strokeWeight: r.selected ? 6 : 4, zIndex: r.selected ? 2 : 1, clickable: true });
      line.addListener('click', () => routeClick.current?.(r.key));
      s.lines.push(line);
    }
    s.map.fitBounds(bounds, 40);
  }

  // a new point from outside (a place picked, typed) moves the pin
  useEffect(() => {
    place(point);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point?.lat, point?.lng]);
  const routeKey = JSON.stringify(routes?.map((r) => [r.key, r.selected]) ?? []);
  useEffect(() => {
    draw(routes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  return <div ref={box} className={className} role="group" aria-label={label} data-testid="map-preview" data-map="google" />;
}
